//src/pages/cadastros/UserFormPage.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  BriefcaseBusiness,
  Check,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Loader2,
  Lock,
  MapPin,
  Phone,
  Plus,
  Save,
  Search,
  Shield,
  Star,
  Trash2,
  User,
  Users,
  Wand2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import { PermissionButton } from "@/components/shared/PermissionButton";

import { useUnits } from "@/hooks/useUnits";
import { useGroups } from "@/hooks/useGroups";
import { useDepartments } from "@/hooks/useDepartments";
import { useJobPositions } from "@/hooks/useJobPositions";
import {
  useCreateUser,
  useUpdateUser,
  useUserDetail,
} from "@/hooks/useUsers";

import type {
  UserDetailOut,
  UserUpsertIn,
} from "@/services/users";

import {
  fetchViaCep,
  searchViaCep,
  BRAZILIAN_STATES,
  type ViaCepResponse,
} from "@/services/viacep";

import {
  maskBRPhone,
  maskCEP,
  maskCNPJ,
  maskCPF,
  normalizeCepToApi,
  normalizeDocToApi,
} from "@/lib/masks";

type PersonType = "PF" | "PJ";
type ContactKind = "email" | "phone" | "whatsapp" | "other";

type ContactForm = {
  kind: ContactKind;
  value: string;
  is_primary: boolean;
};

type AddressForm = {
  label: string;
  zip: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  country: string;
};

type EmploymentForm = {
  employee_code: string;
  admission_date: string;
  job_position_id: string;
  department_id: string;
};

type FormState = {
  person_type: PersonType;
  name: string;
  legal_name: string;
  document_number: string;
  contacts: ContactForm[];
  addresses: AddressForm[];
  login: string;
  password: string;
  confirm_password: string;
  unit_id: string;
  group_ids: string[];
  employment: EmploymentForm;
};

type DeleteTarget =
  | { type: "contact"; index: number }
  | { type: "address"; index: number }
  | null;

const CONTACT_KIND_LABELS: Record<ContactKind, string> = {
  email: "E-mail",
  whatsapp: "WhatsApp",
  phone: "Telefone",
  other: "Outro",
};

const ADDRESS_LABELS = ["Principal", "Matriz", "Filial", "Residencial", "Comercial"];

const emptyAddress = (): AddressForm => ({
  label: "Principal",
  zip: "",
  street: "",
  number: "",
  complement: "",
  district: "",
  city: "",
  state: "",
  country: "BR",
});

const emptyEmployment = (): EmploymentForm => ({
  employee_code: "",
  admission_date: "",
  job_position_id: "",
  department_id: "",
});

const emptyForm = (): FormState => ({
  person_type: "PF",
  name: "",
  legal_name: "",
  document_number: "",
  contacts: [{ kind: "email", value: "", is_primary: true }],
  addresses: [emptyAddress()],
  login: "",
  password: "",
  confirm_password: "",
  unit_id: "",
  group_ids: [],
  employment: emptyEmployment(),
});

function docMaskByType(type: PersonType, value: string) {
  return type === "PJ" ? maskCNPJ(value) : maskCPF(value);
}

function formatContactValue(kind: ContactKind, value: string) {
  if (kind === "whatsapp" || kind === "phone") return maskBRPhone(value);
  return value;
}

function mapAnyAddressToForm(addr: any): AddressForm {
  return {
    label: addr?.label || "Principal",
    zip: addr?.zip ? maskCEP(addr.zip) : "",
    street: addr?.street || "",
    number: addr?.number || "",
    complement: addr?.complement || "",
    district: addr?.district || "",
    city: addr?.city || "",
    state: (addr?.state || "").toUpperCase(),
    country: (addr?.country || "BR").toUpperCase(),
  };
}

function mapDetailToForm(detail: UserDetailOut): FormState {
  const contacts: ContactForm[] =
    Array.isArray(detail.contacts) && detail.contacts.length > 0
      ? detail.contacts.map((c) => ({
          kind: c.kind,
          value: formatContactValue(c.kind, c.value || ""),
          is_primary: !!c.is_primary,
        }))
      : [{ kind: "email", value: "", is_primary: true }];

  if (!contacts.some((c) => c.is_primary)) contacts[0].is_primary = true;

  const addresses: AddressForm[] =
    Array.isArray(detail.addresses) && detail.addresses.length > 0
      ? detail.addresses.map(mapAnyAddressToForm)
      : [emptyAddress()];

  return {
    person_type: detail.person?.person_type || "PF",
    name: detail.person?.name || "",
    legal_name: detail.person?.legal_name || "",
    document_number: docMaskByType(
      detail.person?.person_type || "PF",
      detail.person?.document_number || "",
    ),
    contacts,
    addresses,
    login: detail.user?.email || "",
    password: "",
    confirm_password: "",
    unit_id: detail.user?.unit_id || detail.unit?.unit_id || "",
    group_ids: Array.isArray(detail.group_ids) ? detail.group_ids : [],
    employment: {
      employee_code: detail.employment?.employee_code || "",
      admission_date: detail.employment?.admission_date || "",
      job_position_id: detail.employment?.job_position_id || "",
      department_id: detail.employment?.department_id || "",
    },
  };
}

function isValidEmail(value: string) {
  const s = (value || "").trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function passwordRules(password: string) {
  const s = password || "";
  const hasMin = s.length >= 8;
  const hasUpper = /[A-Z]/.test(s);
  const hasLower = /[a-z]/.test(s);
  const hasNumber = /[0-9]/.test(s);
  return {
    hasMin,
    hasUpper,
    hasLower,
    hasNumber,
    ok: hasMin && hasUpper && hasLower && hasNumber,
  };
}

function generateStrongPassword(len = 12) {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const nums = "23456789";
  const symbols = "!@#$%&*?";
  const pick = (s: string) => s[Math.floor(Math.random() * s.length)];

  let pw = pick(upper) + pick(lower) + pick(nums) + pick(symbols);
  const all = upper + lower + nums + symbols;
  while (pw.length < len) pw += pick(all);

  return pw
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");
}

function toggleId(list: string[], id: string) {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

function dataUrlToPhoto(dataUrl?: string | null) {
  if (!dataUrl) return null;

  const match = dataUrl.match(/^data:(image\/(?:png|jpeg));base64,(.+)$/);
  if (!match) return null;

  const mime = match[1];
  const base64 = match[2];
  const extension = mime === "image/png" ? "png" : "jpg";

  return {
    photo_base64: base64,
    photo_mime_type: mime,
    photo_file_name: `user-photo.${extension}`,
  };
}

function toUpsertBody(
  form: FormState,
  isEditing: boolean,
  photoPreview?: string | null,
): UserUpsertIn {
  const contacts = (form.contacts || [])
    .map((c) => ({
      kind: c.kind,
      value: (c.value || "").trim(),
      is_primary: !!c.is_primary,
    }))
    .filter((c) => c.value.length > 0);

  if (contacts.length === 0) throw new Error("Informe ao menos 1 contato");

  const primaryIdx = contacts.findIndex((c) => c.is_primary);
  if (primaryIdx === -1) contacts[0].is_primary = true;
  if (primaryIdx > -1) contacts.forEach((c, i) => (c.is_primary = i === primaryIdx));

  const addresses = (form.addresses || [])
    .map((a, idx) => ({
      label: (a.label || "").trim() || null,
      zip: normalizeCepToApi(a.zip) || null,
      street: (a.street || "").trim() || null,
      number: (a.number || "").trim() || null,
      complement: (a.complement || "").trim() || null,
      district: (a.district || "").trim() || null,
      city: (a.city || "").trim() || null,
      state: (a.state || "").trim().toUpperCase() || null,
      country: ((a.country || "BR").trim() || "BR").toUpperCase(),
      is_primary: idx === 0,
    }))
    .filter((a) =>
      [
        a.label,
        a.zip,
        a.street,
        a.number,
        a.complement,
        a.district,
        a.city,
        a.state,
        a.country,
      ].some((v) => (v || "").toString().trim().length > 0),
    );

  const body: UserUpsertIn = {
    email: form.login.trim().toLowerCase(),
    is_active: true,
    unit_id: form.unit_id,
    group_ids: form.group_ids,
    person: {
      person_type: form.person_type,
      name: form.name.trim(),
      legal_name: form.legal_name.trim() || null,
      document_number: normalizeDocToApi(form.document_number) || null,
      photo: dataUrlToPhoto(photoPreview),
      contacts,
      addresses,
    },
    employment: {
      employee_code: form.employment.employee_code.trim() || null,
      admission_date: form.employment.admission_date || null,
      job_position_id: form.employment.job_position_id || null,
      department_id: form.employment.department_id || null,
    },
  };

  if (!isEditing || form.password) {
    body.password = form.password;
  }

  return body;
}

export default function UserFormPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const { user } = useAuth();

  const isCreating = !id;
  const isEditing = !!id && location.pathname.endsWith("/editar");
  const isViewing = !!id && !location.pathname.endsWith("/editar");
  const readOnly = isViewing;
  const canWriteUsers = user?.permissions?.includes(PERMISSIONS.USERS_WRITE) ?? false;

  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const contactRefs = useRef<Array<HTMLDivElement | null>>([]);
  const addressRefs = useRef<Array<HTMLDivElement | null>>([]);

  const [highlightedContactIdx, setHighlightedContactIdx] = useState<number | null>(null);
  const [highlightedAddressIdx, setHighlightedAddressIdx] = useState<number | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);

  const unitsQ = useUnits();
  const groupsQ = useGroups();
  const departmentsQ = useDepartments();
  const jobPositionsQ = useJobPositions();
  const detailQ = useUserDetail(id);

  const createMut = useCreateUser();
  const updateMut = useUpdateUser();

  const units = unitsQ.data ?? [];
  const groups = groupsQ.data ?? [];
  const departments = departmentsQ.data ?? [];
  const jobPositions = jobPositionsQ.data ?? [];

  const [cepDialogOpen, setCepDialogOpen] = useState(false);
  const [cepTargetIdx, setCepTargetIdx] = useState(0);
  const [cepSearchMode, setCepSearchMode] = useState<"cep" | "address">("cep");
  const [cepSearchValue, setCepSearchValue] = useState("");
  const [addrSearchUf, setAddrSearchUf] = useState("");
  const [addrSearchCity, setAddrSearchCity] = useState("");
  const [addrSearchStreet, setAddrSearchStreet] = useState("");
  const [cepResults, setCepResults] = useState<ViaCepResponse[]>([]);
  const [cepSearching, setCepSearching] = useState(false);

  useEffect(() => {
    if (!(isEditing || isViewing)) return;
    if (!detailQ.data) return;

    const mapped = mapDetailToForm(detailQ.data);
    mapped.password = "";
    mapped.confirm_password = "";

    setForm(mapped);
    setShowPassword(false);
    setShowConfirmPassword(false);

    const photo = detailQ.data.person?.photo;
    if (photo?.base64 && photo?.mime_type) {
      setPhotoPreview(`data:${photo.mime_type};base64,${photo.base64}`);
    } else {
      setPhotoPreview(null);
    }
  }, [detailQ.data, isEditing, isViewing]);

  useEffect(() => {
    if (detailQ.isError) {
      toast.error("Erro ao carregar usuário");
      navigate("/cadastros/usuarios");
    }
  }, [detailQ.isError, navigate]);

  const pw = useMemo(() => passwordRules(form.password), [form.password]);
  const confirmOk = form.password.length > 0 && form.confirm_password === form.password;

  function goBack() {
    navigate("/cadastros/usuarios");
  }

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    if (readOnly) return;
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function setAddressField(addrIdx: number, key: keyof AddressForm, value: string) {
    if (readOnly) return;
    setForm((prev) => {
      const next = [...prev.addresses];
      next[addrIdx] = { ...next[addrIdx], [key]: value };
      return { ...prev, addresses: next };
    });
  }

  function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (readOnly) return;

    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Use JPG ou PNG.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Máximo 2MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setPhotoPreview(reader.result as string);
      toast.success("Foto carregada");
    };
    reader.readAsDataURL(file);
  }

  function removePhoto() {
    if (readOnly) return;
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function scrollToAddress(idx: number) {
    requestAnimationFrame(() => {
      const el = addressRefs.current[idx];
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedAddressIdx(idx);
      window.setTimeout(() => setHighlightedAddressIdx((current) => (current === idx ? null : current)), 1800);
    });
  }

  function scrollToContact(idx: number) {
    requestAnimationFrame(() => {
      const el = contactRefs.current[idx];
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedContactIdx(idx);
      window.setTimeout(() => setHighlightedContactIdx((current) => (current === idx ? null : current)), 1800);
    });
  }

  function addAddress() {
    if (readOnly) return;
    const nextIdx = form.addresses.length;
    setForm((prev) => ({ ...prev, addresses: [...prev.addresses, emptyAddress()] }));
    toast.success("Novo endereço adicionado");
    window.setTimeout(() => scrollToAddress(nextIdx), 80);
  }

  function removeAddress(idx: number) {
    if (readOnly) return;
    setForm((prev) => {
      const next = [...prev.addresses];
      next.splice(idx, 1);
      return { ...prev, addresses: next.length ? next : [emptyAddress()] };
    });
  }

  function addContact() {
    if (readOnly) return;
    const nextIdx = form.contacts.length;
    setForm((prev) => ({
      ...prev,
      contacts: [...prev.contacts, { kind: "whatsapp", value: "", is_primary: false }],
    }));
    toast.success("Novo contato adicionado");
    window.setTimeout(() => scrollToContact(nextIdx), 80);
  }

  function removeContact(idx: number) {
    if (readOnly) return;
    setForm((prev) => {
      const next = [...prev.contacts];
      next.splice(idx, 1);
      if (next.length > 0 && !next.some((c) => c.is_primary)) next[0].is_primary = true;
      return {
        ...prev,
        contacts: next.length ? next : [{ kind: "email", value: "", is_primary: true }],
      };
    });
  }

  function confirmDeleteTarget() {
    if (!deleteTarget || readOnly) return;

    if (deleteTarget.type === "contact") {
      removeContact(deleteTarget.index);
      toast.success("Contato removido");
    } else {
      removeAddress(deleteTarget.index);
      toast.success("Endereço removido");
    }

    setDeleteTarget(null);
  }

  function setPrimaryContact(idx: number) {
    if (readOnly) return;
    setForm((prev) => ({
      ...prev,
      contacts: prev.contacts.map((c, i) => ({ ...c, is_primary: i === idx })),
    }));
  }

  function updateContact(idx: number, field: keyof ContactForm, value: any) {
    if (readOnly) return;
    setForm((prev) => {
      const next = [...prev.contacts];
      if (field === "kind") {
        next[idx] = {
          ...next[idx],
          kind: value as ContactKind,
          value: formatContactValue(value as ContactKind, next[idx].value),
        };
      } else {
        next[idx] = {
          ...next[idx],
          [field]: field === "value" ? formatContactValue(next[idx].kind, value) : value,
        };
      }
      return { ...prev, contacts: next };
    });
  }

  async function handleCepBlur(addrIdx: number) {
    if (readOnly) return;
    const cep = normalizeCepToApi(form.addresses[addrIdx]?.zip || "");
    if (!cep || cep.length !== 8) return;
    const data = await fetchViaCep(cep);
    if (!data) return;
    applyViaCepResult(data, addrIdx);
  }

  function applyViaCepResult(data: ViaCepResponse, addrIdx: number) {
    if (readOnly) return;
    setForm((prev) => {
      const next = [...prev.addresses];
      const cur = next[addrIdx] || emptyAddress();
      next[addrIdx] = {
        ...cur,
        zip: maskCEP(data.cep || cur.zip),
        street: data.logradouro || cur.street,
        complement: data.complemento || cur.complement,
        district: data.bairro || cur.district,
        city: data.localidade || cur.city,
        state: (data.uf || cur.state).toUpperCase(),
        country: "BR",
      };
      return { ...prev, addresses: next };
    });
  }

  function openCepSearch(addrIdx: number) {
    if (readOnly) return;
    setCepTargetIdx(addrIdx);
    setCepSearchMode("cep");
    setCepSearchValue("");
    setAddrSearchUf("");
    setAddrSearchCity("");
    setAddrSearchStreet("");
    setCepResults([]);
    setCepDialogOpen(true);
  }

  async function handleCepSearch() {
    if (readOnly) return;
    setCepSearching(true);
    setCepResults([]);
    try {
      if (cepSearchMode === "cep") {
        const data = await fetchViaCep(cepSearchValue);
        setCepResults(data ? [data] : []);
        if (!data) toast.error("CEP não encontrado");
      } else {
        const results = await searchViaCep(addrSearchUf, addrSearchCity, addrSearchStreet);
        setCepResults(results);
        if (results.length === 0) {
          toast.error("Nenhum endereço encontrado. Verifique os campos.");
        }
      }
    } finally {
      setCepSearching(false);
    }
  }

  function handleCepSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    handleCepSearch();
  }

  function selectCepResult(result: ViaCepResponse) {
    if (readOnly) return;
    applyViaCepResult(result, cepTargetIdx);
    setCepDialogOpen(false);
    toast.success("Endereço preenchido");
    window.setTimeout(() => scrollToAddress(cepTargetIdx), 80);
  }

  function applyGeneratedPassword() {
    if (readOnly) return;
    const generated = generateStrongPassword(12);
    setField("password", generated);
    setField("confirm_password", generated);
    setShowPassword(false);
    setShowConfirmPassword(false);
    toast.success("Senha forte preenchida automaticamente");
  }

  async function handleSave() {
    if (readOnly) return;

    if (!form.name.trim()) return toast.error("Nome é obrigatório");
    if (!form.legal_name.trim()) return toast.error("Nome legal / razão é obrigatório");
    if (!form.document_number.trim()) return toast.error("Documento é obrigatório");

    if (!form.unit_id) return toast.error("Selecione uma unidade");
    if (!form.group_ids.length) return toast.error("Selecione ao menos 1 grupo de acesso");

    if (!form.login.trim()) return toast.error("Login (e-mail) é obrigatório");
    if (!isValidEmail(form.login)) return toast.error("Informe um e-mail válido");

    if (!isEditing) {
      if (!form.password) return toast.error("Senha é obrigatória");
      if (!pw.ok) return toast.error("Senha não atende aos critérios mínimos");
      if (!confirmOk) return toast.error("Senha e confirmação devem ser iguais");
    } else if (form.password) {
      if (!pw.ok) return toast.error("Senha não atende aos critérios mínimos");
      if (!confirmOk) return toast.error("Senha e confirmação devem ser iguais");
    }

    if (!form.employment.employee_code.trim()) return toast.error("Matrícula é obrigatória");
    if (!form.employment.admission_date.trim()) return toast.error("Data de admissão é obrigatória");
    if (!form.employment.job_position_id) return toast.error("Selecione um cargo");
    if (!form.employment.department_id) return toast.error("Selecione um departamento");

    const contactsClean = form.contacts.filter((c) => (c.value || "").trim().length > 0);
    if (contactsClean.length === 0) return toast.error("Informe ao menos 1 contato");

    setSaving(true);
    try {
      const body = toUpsertBody(form, isEditing, photoPreview);

      if (isEditing) {
        await updateMut.mutateAsync({ id: id!, body });
        toast.success("Usuário atualizado");
      } else if (isCreating) {
        await createMut.mutateAsync(body);
        toast.success("Usuário cadastrado");
      }

      navigate("/cadastros/usuarios");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  if ((isEditing || isViewing) && detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="pb-10">
      <div className="sticky top-14 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b mb-6 -mx-1 px-1">
        <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={goBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold tracking-tight">
                {isViewing ? "Visualizar Usuário" : isEditing ? "Editar Usuário" : "Novo Usuário"}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isViewing
                  ? "Consulta dos dados do usuário"
                  : isEditing
                  ? "Atualize os dados do usuário"
                  : "Preencha os dados para cadastrar o usuário"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end md:self-auto">
            <Button variant="outline" onClick={goBack} disabled={saving}>
              {isViewing ? "Voltar" : "Cancelar"}
            </Button>

            {isViewing ? (
              <PermissionButton
                permission={PERMISSIONS.USERS_WRITE}
                onClick={() => navigate(`/cadastros/usuarios/${id}/editar`)}
              >
                Editar
              </PermissionButton>
            ) : (
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Salvar
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              Identificação
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-5">
            <div className="flex flex-col lg:flex-row items-start gap-6">
              <div className="w-full lg:w-52 shrink-0">
                <Label className="mb-2 block">Foto do usuário</Label>

                <div className="rounded-xl border border-dashed p-3">
                  <div className="aspect-square rounded-lg border bg-muted/30 flex items-center justify-center overflow-hidden">
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="Preview da foto"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-muted-foreground gap-2">
                        <ImageIcon className="h-8 w-8" />
                        <span className="text-xs text-center px-2">
                          JPG ou PNG até 2MB
                        </span>
                      </div>
                    )}
                  </div>

                  {!readOnly ? (
                    <div className="mt-3 flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <ImageIcon className="mr-2 h-4 w-4" />
                        {photoPreview ? "Trocar" : "Enviar"}
                      </Button>

                      {photoPreview && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={removePhoto}
                          className="text-destructive hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ) : null}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    className="hidden"
                    onChange={handlePhotoUpload}
                    disabled={readOnly}
                  />
                </div>
              </div>

              <div className="flex-1 w-full space-y-4">
                <div>
                  <Label className="mb-2 block">Tipo de Pessoa</Label>

                  {isEditing || isViewing ? (
                    <div className="space-y-2">
                      <div className="flex gap-4">
                        <div
                          className={`flex items-center gap-3 rounded-lg border-2 px-4 py-3 ${
                            form.person_type === "PF"
                              ? "border-primary bg-primary/5"
                              : "border-border opacity-50"
                          }`}
                        >
                          <div className="h-4 w-4 rounded-full border flex items-center justify-center">
                            {form.person_type === "PF" ? (
                              <div className="h-2 w-2 rounded-full bg-primary" />
                            ) : null}
                          </div>
                          <User className="h-4 w-4" />
                          <span className="font-medium text-sm">Pessoa Física</span>
                        </div>

                        <div
                          className={`flex items-center gap-3 rounded-lg border-2 px-4 py-3 ${
                            form.person_type === "PJ"
                              ? "border-primary bg-primary/5"
                              : "border-border opacity-50"
                          }`}
                        >
                          <div className="h-4 w-4 rounded-full border flex items-center justify-center">
                            {form.person_type === "PJ" ? (
                              <div className="h-2 w-2 rounded-full bg-primary" />
                            ) : null}
                          </div>
                          <Building2 className="h-4 w-4" />
                          <span className="font-medium text-sm">Pessoa Jurídica</span>
                        </div>
                      </div>

                      {!isViewing ? (
                        <p className="text-xs text-muted-foreground">
                          Em edição, o tipo de pessoa não pode ser alterado. Caso o cadastro tenha sido criado incorretamente,
                          inative o atual e crie um novo cadastro.
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <RadioGroup
                      value={form.person_type}
                      onValueChange={(v) => {
                        const nextType = v as PersonType;
                        setForm((prev) => ({
                          ...prev,
                          person_type: nextType,
                          document_number: docMaskByType(nextType, prev.document_number),
                        }));
                      }}
                      className="flex gap-4"
                    >
                      <label
                        className={`flex items-center gap-3 rounded-lg border-2 px-4 py-3 cursor-pointer transition-colors ${
                          form.person_type === "PF"
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-muted-foreground/30"
                        }`}
                      >
                        <RadioGroupItem value="PF" />
                        <User className="h-4 w-4" />
                        <span className="font-medium text-sm">Pessoa Física</span>
                      </label>

                      <label
                        className={`flex items-center gap-3 rounded-lg border-2 px-4 py-3 cursor-pointer transition-colors ${
                          form.person_type === "PJ"
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-muted-foreground/30"
                        }`}
                      >
                        <RadioGroupItem value="PJ" />
                        <Building2 className="h-4 w-4" />
                        <span className="font-medium text-sm">Pessoa Jurídica</span>
                      </label>
                    </RadioGroup>
                  )}
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>
                      {form.person_type === "PJ" ? "Nome Fantasia" : "Nome Completo"} *
                    </Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      placeholder={
                        form.person_type === "PJ"
                          ? "Nome Fantasia"
                          : "Nome Completo"
                      }
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label>{form.person_type === "PJ" ? "Razão Social" : "Nome Legal"} *</Label>
                    <Input
                      value={form.legal_name}
                      onChange={(e) => setField("legal_name", e.target.value)}
                      placeholder={
                        form.person_type === "PJ"
                          ? "Nome Razão Social"
                          : "Nome Legal"
                      }
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>

                  <div>
                    <Label>{form.person_type === "PJ" ? "CNPJ" : "CPF"} *</Label>
                    <Input
                      value={form.document_number}
                      onChange={(e) =>
                        setField("document_number", docMaskByType(form.person_type, e.target.value))
                      }
                      placeholder={
                        form.person_type === "PJ" ? "00.000.000/0000-00" : "000.000.000-00"
                      }
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Lock className="h-4 w-4 text-primary" />
                Login e Senha
              </CardTitle>

              {!readOnly ? (
                <Button type="button" variant="outline" size="sm" onClick={applyGeneratedPassword}>
                  <Wand2 className="mr-2 h-4 w-4" />
                  Gerar senha forte
                </Button>
              ) : null}
            </div>

            <p className="text-sm text-muted-foreground mt-1">
              {isViewing
                ? "Em modo visualização, a senha não pode ser alterada."
                : isEditing
                ? "No modo edição, preencha a senha apenas se desejar alterá-la."
                : "No cadastro, login e senha são obrigatórios."}
            </p>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Login (e-mail) {!isEditing && !isViewing && "*"}</Label>
                <Input
                  value={form.login}
                  onChange={(e) => setField("login", e.target.value)}
                  placeholder="email@empresa.com"
                  className="mt-1"
                  disabled={readOnly}
                />
              </div>

              {!isViewing ? (
                <>
                  <div>
                    <Label>{isEditing ? "Nova senha (opcional)" : "Senha *"}</Label>
                    <div className="relative mt-1">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={form.password}
                        onChange={(e) => setField("password", e.target.value)}
                        placeholder="********"
                        className="pr-10"
                        autoComplete="new-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1 h-8 w-8"
                        onClick={() => setShowPassword((v) => !v)}
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label>{isEditing ? "Confirma nova senha (opcional)" : "Confirma senha *"}</Label>
                    <div className="relative mt-1">
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        value={form.confirm_password}
                        onChange={(e) => setField("confirm_password", e.target.value)}
                        placeholder="********"
                        className="pr-10"
                        autoComplete="new-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1 h-8 w-8"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </>
              ) : null}
            </div>

            {!isViewing ? (
              <div className="rounded-lg border p-3 text-sm">
                <div className="font-medium mb-2 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Regras de senha
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Check className={`h-4 w-4 ${pw.hasMin ? "opacity-100" : "opacity-30"}`} />
                    Mínimo 8 caracteres
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className={`h-4 w-4 ${pw.hasUpper ? "opacity-100" : "opacity-30"}`} />
                    1 letra maiúscula
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className={`h-4 w-4 ${pw.hasLower ? "opacity-100" : "opacity-30"}`} />
                    1 letra minúscula
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className={`h-4 w-4 ${pw.hasNumber ? "opacity-100" : "opacity-30"}`} />
                    1 número
                  </div>
                  <div className="flex items-center gap-2 sm:col-span-2">
                    <Check
                      className={`h-4 w-4 ${
                        form.password.length === 0 ? "opacity-30" : confirmOk ? "opacity-100" : "opacity-30"
                      }`}
                    />
                    Confirmação igual à senha
                  </div>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              Unidade
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Selecione apenas uma unidade para vincular ao usuário.
            </p>
          </CardHeader>

          <CardContent>
            {unitsQ.isLoading ? (
              <div className="text-sm text-muted-foreground">Carregando unidades...</div>
            ) : unitsQ.isError ? (
              <div className="text-sm text-destructive">Erro ao carregar unidades</div>
            ) : units.length === 0 ? (
              <div className="text-sm text-muted-foreground">Nenhuma unidade cadastrada.</div>
            ) : (
              <div className="rounded-lg border p-3 space-y-2 max-h-[260px] overflow-auto">
                {units.map((u: any) => {
                  const selected = form.unit_id === u.id;

                  return (
                    <button
                      type="button"
                      key={u.id}
                      onClick={() => !readOnly && setField("unit_id", u.id)}
                      disabled={readOnly}
                      className={`w-full text-left rounded-lg border px-3 py-3 transition-colors ${
                        selected
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-muted-foreground/30"
                      } ${readOnly ? "cursor-default" : ""}`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`mt-0.5 h-4 w-4 rounded-full border flex items-center justify-center ${
                            selected ? "border-primary" : "border-muted-foreground/40"
                          }`}
                        >
                          {selected ? <div className="h-2 w-2 rounded-full bg-primary" /> : null}
                        </div>

                        <div className="flex-1">
                          <div className="font-medium text-sm">{u.name}</div>
                          {u.code ? (
                            <div className="text-xs text-muted-foreground">Código: {u.code}</div>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              Grupos de acesso
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Selecione ao menos 1 grupo. É permitido selecionar mais de um.
            </p>
          </CardHeader>

          <CardContent>
            {groupsQ.isLoading ? (
              <div className="text-sm text-muted-foreground">Carregando grupos...</div>
            ) : groupsQ.isError ? (
              <div className="text-sm text-destructive">Erro ao carregar grupos</div>
            ) : groups.length === 0 ? (
              <div className="text-sm text-muted-foreground">Nenhum grupo cadastrado.</div>
            ) : (
              <div className="rounded-lg border p-3 space-y-2 max-h-[260px] overflow-auto">
                {groups.map((g: any) => {
                  const checked = form.group_ids.includes(g.id);
                  return (
                    <label key={g.id} className="flex items-start gap-3 py-2 cursor-pointer">
                      <Checkbox
                        checked={checked}
                        disabled={readOnly}
                        onCheckedChange={() => !readOnly && setField("group_ids", toggleId(form.group_ids, g.id))}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">{g.name}</div>
                        {g.description ? (
                          <div className="text-xs text-muted-foreground">{g.description}</div>
                        ) : null}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <BriefcaseBusiness className="h-4 w-4 text-primary" />
              Dados do funcionário
            </CardTitle>
          </CardHeader>

          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Matrícula *</Label>
              <Input
                value={form.employment.employee_code}
                onChange={(e) =>
                  setField("employment", { ...form.employment, employee_code: e.target.value })
                }
                placeholder="Ex.: EMP-00015"
                className="mt-1"
                disabled={readOnly}
              />
            </div>

            <div>
              <Label>Data de admissão *</Label>
              <Input
                type="date"
                value={form.employment.admission_date}
                onChange={(e) =>
                  setField("employment", { ...form.employment, admission_date: e.target.value })
                }
                className="mt-1"
                disabled={readOnly}
              />
            </div>

            <div>
              <Label>Cargo *</Label>
              <Select
                value={form.employment.job_position_id || undefined}
                onValueChange={(value) =>
                  setField("employment", { ...form.employment, job_position_id: value })
                }
                disabled={readOnly}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione um cargo" />
                </SelectTrigger>
                <SelectContent>
                  {jobPositions.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {jobPositionsQ.isLoading ? (
                <div className="text-xs text-muted-foreground mt-1">Carregando cargos...</div>
              ) : null}
            </div>

            <div>
              <Label>Departamento *</Label>
              <Select
                value={form.employment.department_id || undefined}
                onValueChange={(value) =>
                  setField("employment", { ...form.employment, department_id: value })
                }
                disabled={readOnly}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione um departamento" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {departmentsQ.isLoading ? (
                <div className="text-xs text-muted-foreground mt-1">Carregando departamentos...</div>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Phone className="h-4 w-4 text-primary" />
                Contatos
              </CardTitle>
              {!readOnly ? (
                <Button variant="outline" size="sm" onClick={addContact}>
                  <Plus className="mr-1 h-4 w-4" />
                  Adicionar
                </Button>
              ) : null}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Informe ao menos 1 contato e defina qual é o principal.
            </p>
          </CardHeader>

          <CardContent className="space-y-3">
            {form.contacts.map((c, idx) => (
              <div
                key={idx}
                ref={(el) => (contactRefs.current[idx] = el)}
                className={`flex flex-col sm:flex-row items-start sm:items-end gap-3 rounded-lg border p-3 transition-all ${
                  highlightedContactIdx === idx
                    ? "border-primary ring-2 ring-primary/20 bg-primary/5"
                    : c.is_primary
                    ? "border-primary/30 bg-primary/5"
                    : "border-border"
                }`}
              >
                <div className="w-full sm:w-36">
                  <Label className="text-xs">Tipo</Label>
                  <Select
                    value={c.kind}
                    onValueChange={(v) => updateContact(idx, "kind", v)}
                    disabled={readOnly}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CONTACT_KIND_LABELS).map(([k, l]) => (
                        <SelectItem key={k} value={k}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 w-full">
                  <Label className="text-xs">Valor</Label>
                  <Input
                    value={c.value}
                    onChange={(e) => updateContact(idx, "value", e.target.value)}
                    placeholder={
                      c.kind === "email"
                        ? "email@exemplo.com"
                        : c.kind === "whatsapp" || c.kind === "phone"
                        ? "(51) 99999-9999"
                        : "Contato"
                    }
                    className="mt-1"
                    disabled={readOnly}
                  />
                </div>

                {!readOnly ? (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      type="button"
                      variant={c.is_primary ? "default" : "outline"}
                      size="sm"
                      onClick={() => setPrimaryContact(idx)}
                      className="gap-1"
                    >
                      <Star className={`h-3 w-3 ${c.is_primary ? "fill-current" : ""}`} />
                      {c.is_primary ? "Principal" : "Definir"}
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget({ type: "contact", index: idx })}
                      disabled={form.contacts.length <= 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                Endereços
              </CardTitle>
              {!readOnly ? (
                <Button variant="outline" size="sm" onClick={addAddress}>
                  <Plus className="mr-1 h-4 w-4" />
                  Adicionar
                </Button>
              ) : null}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Preencha manualmente ou use a busca de CEP.
            </p>
          </CardHeader>

          <CardContent className="space-y-4">
            {form.addresses.map((addr, addrIdx) => (
              <div
                key={addrIdx}
                ref={(el) => (addressRefs.current[addrIdx] = el)}
                className={`rounded-lg border p-4 space-y-4 transition-all ${
                  highlightedAddressIdx === addrIdx
                    ? "border-primary ring-2 ring-primary/20 bg-primary/5"
                    : "border-border"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      Endereço {addrIdx + 1}
                    </Badge>
                    {addrIdx === 0 && <Badge className="text-xs">Principal</Badge>}
                  </div>

                  {!readOnly ? (
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => openCepSearch(addrIdx)}>
                        <Search className="mr-1 h-3.5 w-3.5" />
                        Buscar CEP
                      </Button>

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget({ type: "address", index: addrIdx })}
                        disabled={form.addresses.length <= 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </div>

                <div className="grid grid-cols-6 gap-4">
                  <div className="col-span-6 sm:col-span-2">
                    <Label className="text-xs">Rótulo</Label>
                    <Select
                      value={addr.label}
                      onValueChange={(v) => setAddressField(addrIdx, "label", v)}
                      disabled={readOnly}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ADDRESS_LABELS.map((l) => (
                          <SelectItem key={l} value={l}>
                            {l}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="col-span-6 sm:col-span-2">
                    <Label className="text-xs">CEP</Label>
                    <Input
                      value={addr.zip}
                      onChange={(e) => setAddressField(addrIdx, "zip", maskCEP(e.target.value))}
                      onBlur={() => handleCepBlur(addrIdx)}
                      placeholder="00000-000"
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>

                  <div className="col-span-6">
                    <Label className="text-xs">Logradouro</Label>
                    <Input
                      value={addr.street}
                      onChange={(e) => setAddressField(addrIdx, "street", e.target.value)}
                      placeholder="Rua / Avenida"
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-2">
                    <Label className="text-xs">Número</Label>
                    <Input
                      value={addr.number}
                      onChange={(e) => setAddressField(addrIdx, "number", e.target.value)}
                      placeholder="123"
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-2">
                    <Label className="text-xs">Complemento</Label>
                    <Input
                      value={addr.complement}
                      onChange={(e) => setAddressField(addrIdx, "complement", e.target.value)}
                      placeholder="Apto / Sala / Bloco"
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-2">
                    <Label className="text-xs">Bairro</Label>
                    <Input
                      value={addr.district}
                      onChange={(e) => setAddressField(addrIdx, "district", e.target.value)}
                      placeholder="Bairro"
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <Label className="text-xs">Cidade</Label>
                    <Input
                      value={addr.city}
                      onChange={(e) => setAddressField(addrIdx, "city", e.target.value)}
                      placeholder="Cidade"
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-1">
                    <Label className="text-xs">UF</Label>
                    <Select
                      value={addr.state}
                      onValueChange={(v) => setAddressField(addrIdx, "state", v)}
                      disabled={readOnly}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="UF" />
                      </SelectTrigger>
                      <SelectContent>
                        {BRAZILIAN_STATES.map((uf) => (
                          <SelectItem key={uf.value} value={uf.value}>
                            {uf.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="col-span-6 sm:col-span-2">
                    <Label className="text-xs">País</Label>
                    <Input
                      value={addr.country}
                      onChange={(e) => setAddressField(addrIdx, "country", e.target.value.toUpperCase())}
                      placeholder="BR"
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={deleteTarget?.type === "contact" ? "Remover contato" : "Remover endereço"}
        description={
          deleteTarget?.type === "contact"
            ? "Tem certeza que deseja remover este contato?"
            : "Tem certeza que deseja remover este endereço?"
        }
        onConfirm={confirmDeleteTarget}
        confirmLabel="Remover"
        variant="destructive"
      />
    </div>
  );
}
